(() => {

  if (customElements.get('oct-salford-rum')) return;

  const emoji = /[\p{Extended_Pictographic}\p{Regional_Indicator}\uFE0F\u20E3\u200D]/u;

  class OctSalfordRum extends HTMLElement {

    connectedCallback() {

      if (this.initialized) return;

      this.initialized = true;

      this.config = JSON.parse(this.querySelector('[data-config]').textContent);

      this.strings = this.config.strings;

      this.form = this.querySelector('form');

      this.variantInput = this.querySelector('[data-variant]');

      this.quantity = this.form.elements.quantity;

      this.dialog = this.querySelector('dialog');

      this.message = this.querySelector('[data-message]');

      this.addButton = this.querySelector('[data-add]');

      this.current = this.config.variants.find(v => v.id === this.config.selectedId) || this.config.variants[0];

    // Always enter the PDP on the matching non-engraved variant.
    // This prevents an engraved variant in the URL/history from keeping the +£15 price.
    if (this.isEngraved(this.current)) {
      const plainVariant = this.pair(false);
      if (plainVariant?.available) this.current = plainVariant;
    }

      this.savedMessage = '';

      this.choiceMade = false;

      this.pendingAdd = false;

      this.busy = false;

      this.previousFocus = null;

      this.querySelector('[data-fallback]').hidden = true;

      this.querySelector('[data-options]').hidden = false;

      this.variantInput.querySelectorAll('option').forEach(option => { option.disabled = false; });

      this.querySelectorAll('[data-option-index]').forEach(select => select.addEventListener('change', () => this.changeOption()));

      // The latest PDP has an inline engraving accordion that can switch the
      // underlying variant directly. Keep this component's state in sync.
      this.variantInput.addEventListener('change', () => {
        const variant = this.config.variants.find(v => String(v.id) === String(this.variantInput.value));
        if (variant) this.current = variant;

        // Defer property syncing because the inline engraving UI updates the
        // line-item property immediately after changing the variant.
        queueMicrotask(() => this.syncInlineEngraving());
      });

      this.querySelectorAll('[data-quantity-change]').forEach(button => button.addEventListener('click', () => {

        if (Number(button.dataset.quantityChange) > 0) this.quantity.stepUp(); else this.quantity.stepDown();

        this.render();

      }));

      this.quantity.addEventListener('input', () => this.render());

      // BSS may insert its discounted amount beside rather than inside [data-price].
      // Observe the whole price wrapper and only rerender when the detected trade
      // amount actually changes, avoiding a mutation/render feedback loop.
      this.bssPriceNode = this.querySelector('.oct-rum__price');
      this.lastObservedTradePrice = this.getDisplayedB2BPrice();
      if (this.bssPriceNode) {
        this.bssObserver = new MutationObserver(() => {
          const trade = this.getDisplayedB2BPrice();
          if (trade === this.lastObservedTradePrice) return;
          this.lastObservedTradePrice = trade;
          this.render();
        });
        this.bssObserver.observe(this.bssPriceNode, { subtree: true, childList: true, characterData: true });
      }

      this.form.addEventListener('submit', event => {

        event.preventDefault();

        if (this.busy || !this.form.reportValidity()) return;
        if (!this.bottles.some(bottle => bottle.engraved) && !this.choiceMade && this.pair(true)?.available) return this.open(true, 'offer');
        this.addToCart();

      });

      this.querySelector('[data-open-engraving]')?.addEventListener('click', () => this.open(false, 'offer'));

      this.querySelector('[data-edit]')?.addEventListener('click', () => this.open(false, 'message'));

      this.querySelector('[data-remove]')?.addEventListener('click', () => this.selectPlain());

      this.querySelector('[data-choose]')?.addEventListener('click', () => this.openBottlePopup());

      this.querySelector('[data-back]')?.addEventListener('click', () => this.step('offer'));

      this.querySelector('[data-close]')?.addEventListener('click', () => this.close(true));

      this.querySelector('[data-skip]').addEventListener('click', () => {

        const pending = this.pendingAdd;

        if (!this.selectPlain()) return;

        this.close();

        if (pending) this.addToCart();

      });

      this.querySelector('[data-save]')?.addEventListener('click', () => this.save());

      this.message.addEventListener('input', () => {

        this.querySelector('[data-count]').textContent = Array.from(this.message.value).length;

        this.message.setAttribute('aria-invalid', 'false');

        this.querySelector('[data-message-error]').hidden = true;

      });

      this.message.addEventListener('keydown', event => { if (event.key === 'Enter') { event.preventDefault(); this.save(); } });

      this.dialog.addEventListener('cancel', event => { event.preventDefault(); this.close(true); });

      this.dialog.addEventListener('click', event => { if (event.target === this.dialog) this.close(true); });

      this.dialog.addEventListener('close', () => {

        document.documentElement.classList.remove('oct-rum__dialog-open');
        this.previousFocus?.focus({ preventScroll: true });
        this.restoreBottleEditor();

        this.pendingAdd = false;

      });

      this.querySelectorAll('[data-media-button]').forEach(button => button.addEventListener('click', () => this.showMedia(button.dataset.mediaButton)));
    this.initializeTabs();
    this.initializeBottles();

    // Browsers can restore this page with the previous engraved state when using Back.
    // Clear that state so engraving must be deliberately selected again.
    this._pageshowHandler = () => {
      if (!this.isConnected) return;

      if (this.isEngraved(this.current) || this.savedMessage) {
        const plain = this.pair(false);

        if (plain?.available) {
          this.current = plain;
          this.savedMessage = '';
          this.choiceMade = false;
          this.message.value = '';
          this.render(true);
        }
      }
    };

    window.addEventListener('pageshow', this._pageshowHandler);

    this.render();

    }

    disconnectedCallback() {
      this.bssObserver?.disconnect();

      if (this.dialog?.open) this.dialog.close();

      document.documentElement.classList.remove('oct-rum__dialog-open');

    if (this._pageshowHandler) {
      window.removeEventListener('pageshow', this._pageshowHandler);
    }

    }

    hasInlineEngraving() {

      const property = this.querySelector('[data-engraving-property]');
      const selected = this.dataset.inlineEngravingSelected === 'true';

      return selected && this.isEngraved(this.current) && Boolean(property?.value?.trim());

    }

    syncInlineEngraving() {

      const selectedVariant = this.config.variants.find(v => String(v.id) === String(this.variantInput?.value));

      if (selectedVariant) this.current = selectedVariant;

      const property = this.querySelector('[data-engraving-property]');
      const inlineSelected = this.dataset.inlineEngravingSelected === 'true';

      if (inlineSelected && property?.value?.trim() && this.isEngraved(this.current)) {
        this.savedMessage = property.value.trim();
        this.choiceMade = true;
      } else if (!this.isEngraved(this.current)) {
        // Do not carry an old modal engraving message onto a plain variant.
        if (inlineSelected === false || this.dataset.inlineEngravingSelected === 'false') {
          this.savedMessage = '';
          this.choiceMade = false;
        }
      }

    }

    isEngraved(variant) {

      return this.config.engravingIndex >= 0 && /^add engraving\b/i.test(variant?.options[this.config.engravingIndex] || '');

    }

    pair(engraved) {

      if (this.config.engravingIndex < 0) return null;

      const selections = this.current?.options || this.config.variants[0].options;

      return this.config.variants.find(v => v.options.every((value, index) => {

        if (index === this.config.engravingIndex) return engraved ? /^add engraving\b/i.test(value) : value.toLowerCase() === 'no engraving';

        const select = this.querySelector(`[data-option-index="${index}"]`);

        return value === (select ? select.value : selections[index]);

      }));

    }

    changeOption() {

      const engravingValue = this.current?.options[this.config.engravingIndex] || (this.savedMessage ? this.config.variants.find(v => this.isEngraved(v))?.options[this.config.engravingIndex] : 'No engraving');

      this.current = this.config.variants.find(v => v.options.every((value, index) => {

        if (index === this.config.engravingIndex) return value === engravingValue;

        const select = this.querySelector(`[data-option-index="${index}"]`);

        return !select || select.value === value;

      }));

      this.choiceMade = Boolean(this.savedMessage);

      this.render(true);

    }

    money(cents) {

      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', { style: 'currency', currency: this.config.currency }).format(cents / 100);

    }

    getDisplayedB2BPrice() {
      const wrapper = this.querySelector('.oct-rum__price');
      if (!wrapper) return null;
      const retail = (this.pair(false)?.price ?? this.current?.price ?? 0) / 100;
      const extract = text => [...String(text || '').matchAll(/(?:£|\$|€)\s*([\d,]+(?:\.\d{1,2})?)/g)]
        .map(match => Number(match[1].replace(/,/g, '')))
        .filter(value => Number.isFinite(value) && value > 0);

      // Read all displayed prices: BSS can place the reduced price in a sibling
      // of [data-price]. Ignore struck-through/old prices where possible.
      const candidates = [];
      const walker = document.createTreeWalker(wrapper, NodeFilter.SHOW_TEXT);
      let node;
      while ((node = walker.nextNode())) {
        const parent = node.parentElement;
        if (!parent || parent.closest('s, del, [data-compare], [hidden], [aria-hidden="true"]')) continue;
        if (getComputedStyle(parent).display === 'none') continue;
        candidates.push(...extract(node.textContent));
      }
      // Prefer the final active non-retail amount. When there is only retail,
      // continue to use the standard variant price rather than infer a discount.
      const trade = candidates.filter(value => Math.abs(value - retail) > 0.001).at(-1);
      return trade ?? null;
    }

    renderBase(updateMedia = false) {

      const v = this.current;

      if (v) {

        this.variantInput.value = String(v.id);

        this.quantity.min = v.min;

        this.quantity.step = v.step;

        if (v.max != null) this.quantity.max = v.max; else this.quantity.removeAttribute('max');

        const priceNode = this.querySelector('[data-price]');
        const originalRetail = this.money(v.price);
        if (!this.getDisplayedB2BPrice() || priceNode.dataset.variantId !== String(v.id)) {
          // Do not overwrite BSS-injected trade markup on same variant.
          if (priceNode.dataset.variantId !== String(v.id) || !priceNode.textContent.trim()) priceNode.textContent = originalRetail;
        }
        priceNode.dataset.variantId = String(v.id);
        priceNode.setAttribute('bss-b2b-variant-id', String(v.id));

        const compare = this.querySelector('[data-compare]');

        compare.textContent = this.money(v.compare);

        compare.hidden = !(v.compare > v.price);

        if (updateMedia && v.mediaId) this.showMedia(String(v.mediaId));

        const url = new URL(location.href);

        url.searchParams.set('variant', v.id);

        history.replaceState({}, '', url);

      } else {

        this.variantInput.value = '';

        this.querySelector('[data-price]').textContent = this.strings.unavailable;

        this.querySelector('[data-compare]').hidden = true;

      }

      this.addButton.disabled = this.busy || !v?.available;

      this.addButton.textContent = this.busy ? this.strings.adding : !v ? this.strings.unavailable : !v.available ? this.strings.sold_out : `${this.strings.add} - ${this.money(v.price * Math.max(1, Number(this.quantity.value) || 1))}`;

      const plain = this.pair(false), engraved = this.pair(true);

      this.querySelectorAll('[data-fee]').forEach(el => { el.textContent = plain && engraved ? `+${this.money(engraved.price - plain.price)}` : ''; });

      const opener = this.querySelector('[data-open-engraving]');

      if (opener) opener.hidden = !engraved?.available || !plain || Boolean(this.savedMessage);

      const chosen = this.querySelector('[data-chosen]');

      if (chosen) chosen.hidden = !this.savedMessage;

      this.querySelector('[data-saved-message]')?.replaceChildren(document.createTextNode(this.savedMessage));

      const statusInput = this.querySelector('[data-engraving-status]');
      const property = this.querySelector('[data-engraving-property]');
      const inlineSelected = this.dataset.inlineEngravingSelected === 'true';

      if (inlineSelected && property?.value?.trim() && this.isEngraved(v)) {
        statusInput.value = 'Yes';
        property.disabled = false;
        this.savedMessage = property.value.trim();
        this.choiceMade = true;
      } else {
        statusInput.value = this.isEngraved(v) ? 'Yes' : 'No';
        property.disabled = !this.isEngraved(v) || !this.savedMessage;
        property.value = this.savedMessage;
      }

      this.querySelector('[data-choose]').disabled = !engraved?.available || this.busy;

      this.querySelector('[data-save]').disabled = !engraved?.available || this.busy;

      this.querySelector('[data-skip]').disabled = !plain?.available || this.busy;

      this.querySelector('[data-each]').hidden = true;

    }

    open(pending, step) {

      this.pendingAdd = pending;

      this.previousFocus = document.activeElement;

      this.message.value = this.savedMessage;

      this.querySelector('[data-count]').textContent = Array.from(this.savedMessage).length;

      this.querySelector('[data-message-error]').hidden = true;

      this.querySelector('[data-dialog-error]').hidden = true;

      this.message.setAttribute('aria-invalid', 'false');

      this.render();

      this.dialog.showModal();

      document.documentElement.classList.add('oct-rum__dialog-open');

      this.step(step);

    }

    step(name) {

      this.querySelectorAll('[data-step]').forEach(el => { el.hidden = el.dataset.step !== name; });

      this.querySelector('[data-back]').hidden = name !== 'message';

      const heading = this.querySelector(`[data-step="${name}"] h2`);

      this.dialog.setAttribute('aria-labelledby', heading.id);

      if (name === 'message') (this.popupBottleHost?.querySelector('input:not([hidden])') || this.message).focus(); else this.querySelector('[data-choose]').focus();

    }

    close(addPlainOnDismiss = false) {
      const shouldAddPlain = addPlainOnDismiss && this.pendingAdd && !this.busy;
      if (shouldAddPlain && !this.selectPlain()) return;
      this.pendingAdd = false;
      if (this.dialog.open) this.dialog.close();
      if (shouldAddPlain) this.addToCart();
    }

    selectPlain() {

      const plain = this.pair(false);

      if (!plain?.available) return false;

      this.current = plain;

      this.savedMessage = '';
      this.bottles?.forEach(bottle => { bottle.engraved=false; bottle.message=''; });
      this.choiceMade = true;

      this.render(true);

      return true;

    }

    validateMessage() {

      const value = this.message.value;

      const error = !value.trim() ? this.strings.empty : Array.from(value).length > 50 ? this.strings.too_long : emoji.test(value) ? this.strings.no_emoji : '';

      const target = this.querySelector('[data-message-error]');

      target.textContent = error;

      target.hidden = !error;

      this.message.setAttribute('aria-invalid', String(Boolean(error)));

      if (error) this.message.focus();

      return !error;

    }

    save() {

      if (!this.validateMessage()) return;

      const variant = this.pair(true);

      if (!variant?.available) {

        const error = this.querySelector('[data-dialog-error]');

        error.textContent = this.strings.engraving_unavailable;

        error.hidden = false;

        return;

      }

      const pending = this.pendingAdd;

      this.savedMessage = this.message.value;

      this.current = variant;

      this.choiceMade = true;

      this.render(true);

      this.close();

      if (pending) this.addToCart();

    }

    async legacyAddToCart() {

      this.syncInlineEngraving();

      if (this.busy || !this.current?.available || !this.form.reportValidity()) return;

      if (this.isEngraved(this.current) && (!this.savedMessage.trim() || emoji.test(this.savedMessage) || Array.from(this.savedMessage).length > 50)) return this.open(true, 'message');

      this.busy = true;

      this.render();

      const error = this.querySelector('[data-cart-error]');

      error.hidden = true;

      this.querySelector('[data-cart-success]').hidden = true;

      try {

        const data = new FormData(this.form);

        const drawer = document.querySelector('cart-drawer');

        const sections = drawer?.getSectionsToRender?.() || [];

        if (sections.length) {

          data.set('sections', sections.map(section => section.id).join(','));

          data.set('sections_url', location.pathname);

        }

        const response = await fetch(this.dataset.addUrl, { method: 'POST', headers: { Accept: 'application/json' }, body: data });

        const result = await response.json();

        if (!response.ok || result.status >= 400) throw new Error(result.description || this.strings.cart_error);

        this.querySelector('[data-cart-success]').hidden = false;

        // A section-rendering failure must never turn a successful add into a retry.

        if (drawer && sections.length) {

          try {

            const complete = sections.every(section => typeof result.sections?.[section.id] === 'string');

            if (complete) {

              drawer.classList.remove('is-empty');

              drawer.setActiveElement?.(this.addButton);

              // The theme's patched renderer guards reopening after a user dismissal.

              drawer._closedByUser = false;

              drawer.renderContents(result);

              drawer.open(this.addButton);

            }

          } catch (_) {

            // Keep the confirmed success and View cart link available on this page.

          }

        }

      } catch (failure) {

        error.textContent = failure.message || this.strings.cart_error;

        error.hidden = false;

      } finally {

        this.busy = false;

        this.render();

      }

    }

    initializeBottles() {
      this.bottles = [];
      this.inline = this.querySelector('[data-inline-engraving]');
      if (!this.inline) return;
      this.bottlePanel = this.inline.querySelector('[data-inline-engraving-panel]');
      const inner = this.inline.querySelector('.oct-rum__engraving-panel-inner');
      this.bottleEditor = inner;
      this.bottleHome = inner.parentElement;
      inner.replaceChildren();
      this.bottleRows = document.createElement('div');
      inner.append(this.bottleRows);
      const notice = document.createElement('div');
      notice.className = 'oct-rum__notice';
      notice.textContent = 'Up to 50 characters per bottle. No emojis.';
      const dispatch = document.createElement('p');
      dispatch.textContent = 'Engraving dispatch: Thursdays. Order by Wednesday 9am.';
      notice.append(dispatch);
      inner.append(notice);
      const save = document.createElement('button');
      this.bottleSave = save;
      save.type = 'button'; save.className = 'oct-rum__button'; save.textContent = 'ADD ENGRAVING: £15';
      save.addEventListener('click', () => {
        if (!this.validateBottles()) return;
        this.choiceMade = true;
        this.bottlePanel.hidden = true;
        this.inline.querySelector('[data-inline-engraving-toggle]').setAttribute('aria-expanded', 'false');
        const add = this.bottlePending; this.bottlePending = false;
        if (this.dialog.open) this.close();
        this.render();
        if (add) this.addToCart();
      });
      inner.append(save);
      this.inline.querySelector('[data-inline-engraving-added]')?.setAttribute('hidden','');
      this.inline.querySelector('[data-inline-engraving-toggle]').addEventListener('click', () => {
        if (this.bottlePanel.hidden) this.openBottlePanel();
        else { this.bottlePanel.hidden = true; this.inline.querySelector('[data-inline-engraving-toggle]').setAttribute('aria-expanded','false'); }
      });
    }
    restoreBottleEditor() {
      if (this.bottleEditor && this.bottleHome) this.bottleHome.append(this.bottleEditor);
    }
    openBottlePopup() {
      this.bottlePending = this.pendingAdd;
      const step = this.querySelector('[data-step="message"]');
      if (!this.popupBottleHost) {
        [...step.children].forEach(child => { if (!child.matches('h2, .oct-rum__eyebrow')) child.hidden = true; });
        this.popupBottleHost = document.createElement('div');
        step.append(this.popupBottleHost);
      }
      this.popupBottleHost.append(this.bottleEditor);
      if (Number(this.quantity.value) === 1 && this.pair(true)?.available) this.bottles[0].engraved = true;
      this.render();
      this.step('message');
    }
    openBottlePanel(pending = false) {
      if (this.dialog.open && this.popupBottleHost?.contains(this.bottleEditor)) {
        this.step('message');
        return;
      }
      this.restoreBottleEditor();
      this.bottlePending = pending;
      if (this.dialog.open) this.close();
      this.render();
      if (!this.bottlePanel) return;
      this.bottlePanel.hidden = false;
      this.inline.querySelector('[data-inline-engraving-toggle]').setAttribute('aria-expanded','true');
      this.bottleRows.querySelector('input')?.focus();
    }
    render(updateMedia = false) {
      this.renderBase(updateMedia);
      if (!this.bottles) return;
      const quantity = Math.max(1, Math.floor(Number(this.quantity.value) || 1));
      this.bottles.length = Math.min(this.bottles.length, quantity);
      while (this.bottles.length < quantity) this.bottles.push({engraved:false, message:''});
      const plain = this.pair(false), engraved = this.pair(true);
      if (!this.bottleRows) return;
      while (this.bottleRows.children.length > quantity) this.bottleRows.lastElementChild.remove();
      for (let index = this.bottleRows.children.length; index < quantity; index++) {
        const row = document.createElement('div'); row.className = 'oct-rum__bottle-row';
        const label = document.createElement('label');
        const check = document.createElement('input'); check.type = 'checkbox';
        const text = document.createElement('span'); label.append(check, text);
        const input = document.createElement('input'); input.type = 'text'; input.maxLength = 50; input.className = 'oct-rum__message';
        input.setAttribute('aria-label', `Engraving message for Bottle ${index + 1}`);
        const hint = document.createElement('small');
        row.append(label, input, hint); this.bottleRows.append(row);
        check.addEventListener('change', () => { this.bottles[index].engraved = check.checked; if (!check.checked) this.bottles[index].message = ''; this.render(); });
        input.addEventListener('input', () => { this.bottles[index].message = input.value; this.render(); });
      }
      [...this.bottleRows.children].forEach((row,index) => {
        const bottle = this.bottles[index];
        const [check,input] = row.querySelectorAll('input');
        check.hidden = quantity === 1 && Boolean(this.popupBottleHost?.contains(this.bottleEditor));
        row.querySelector('label').hidden = check.hidden;
        check.checked = bottle.engraved; check.disabled = this.busy || !engraved?.available;
        row.querySelector('span').textContent = `Bottle ${index+1}: Add hand engraving for £15`;
        input.hidden = !bottle.engraved; input.disabled = this.busy || !bottle.engraved;
        if (input.value !== bottle.message) input.value = bottle.message;
        input.setCustomValidity(bottle.engraved && (!bottle.message.trim() || emoji.test(bottle.message) || Array.from(bottle.message).length > 50) ? 'Enter 1-50 characters without emojis.' : '');
        row.querySelector('small').textContent = bottle.engraved ? `${Array.from(bottle.message).length}/50 · No emojis` : '';
      });
      const count = this.bottles.filter(bottle => bottle.engraved).length;
      if (this.bottleSave) {
        const fee = count * Math.max(0, (engraved?.price || 0) - (plain?.price || 0));
        this.bottleSave.textContent = count ? `ADD ENGRAVING: +${this.money(fee)}` : 'CONTINUE WITHOUT ENGRAVING';
        this.bottleSave.disabled = Boolean(this.busy);
      }
      const total = (quantity-count)*(plain?.price || 0)+count*(engraved?.price || 0);
      this.addButton.disabled = this.busy || this.uncertain || (count < quantity && !plain?.available) || (count > 0 && !engraved?.available);
      // BSS can render both retail and trade values inside [data-price].
      // Read the LAST currency amount (the B2B value), never concatenate the whole node.
      const tradeUnit = this.getDisplayedB2BPrice();
      const regularUnit = (plain?.price || 0) / 100;
      const effectiveTotal = tradeUnit !== null
        ? total / 100 + (tradeUnit - regularUnit) * quantity
        : total / 100;
      this.addButton.textContent = this.busy ? this.strings.adding : this.addButton.disabled ? this.strings.unavailable : `${this.strings.add} - ${this.money(Math.round(effectiveTotal * 100))}`;
      const summary = this.inline.querySelector('[data-inline-engraving-summary]');
      summary.textContent = `${count} of ${quantity} bottles engraved`;
      summary.hidden = !count;
      this.inline.querySelector('[data-inline-engraving-title]').hidden = Boolean(count);
    }
    validateBottles() {
      const invalid = [...(this.bottleRows?.querySelectorAll('input') || [])].find(input => !input.checkValidity());
      if (invalid) { this.openBottlePanel(this.bottlePending); invalid.reportValidity(); return false; }
      return true;
    }
    buildBottleItems() {
      const plain = this.pair(false) || this.current, engraved = this.pair(true);
      const items = [];
      const plainCount = this.bottles.filter(bottle => !bottle.engraved).length;
      if (plainCount) items.push({id:plain.id, quantity:plainCount, properties:{Engraving:'No'}});
      this.bottles.forEach((bottle,index) => {
        if (bottle.engraved) items.push({id:engraved.id, quantity:1, properties:{Engraving:'Yes', _Bottle:String(index+1), 'Engraving text':bottle.message}});
      });
      return items;
    }
    async addToCart() {
      if (this.busy || this.uncertain || !this.form.reportValidity()) return;
      this.render();
      if (this.addButton.disabled || !this.validateBottles()) return;
      const items = this.buildBottleItems();
      this.busy = true; this.render();
      const error = this.querySelector('[data-cart-error]'); error.hidden = true;
      const success = this.querySelector('[data-cart-success]'); success.hidden = true;
      const drawer = document.querySelector('cart-drawer');
      const sections = drawer?.getSectionsToRender?.().map(section=>section.id) || [];
      try {
        const response = await fetch(this.dataset.addUrl, {method:'POST', headers:{'Content-Type':'application/json',Accept:'application/json'},body:JSON.stringify({items,sections,sections_url:location.pathname})});
        const result = await response.json();
        if (!response.ok || result.status >= 400) { error.textContent = result.description || this.strings.cart_error; error.hidden = false; return; }
        success.hidden = false;
        if (drawer && sections.length && sections.every(id=>typeof result.sections?.[id] === 'string')) {
          try { drawer.classList.remove('is-empty'); drawer._closedByUser=false; drawer.setActiveElement?.(this.addButton); drawer.renderContents({...result,id:items[0].id}); drawer.open(this.addButton); } catch (_) {}
        }
      } catch (_) {
        this.uncertain = true;
        error.textContent = 'Unable to confirm the cart response. Check your cart before reloading to try again.'; error.hidden = false;
      } finally { this.busy=false; this.render(); }
    }

    showMedia(id) {

      if (!this.querySelector(`[data-media="${id}"]`)) return;

      this.querySelectorAll('[data-media]').forEach(el => {

        el.hidden = el.dataset.media !== id;

        if (el.hidden) el.querySelectorAll('video').forEach(video => video.pause());

      });

      this.querySelectorAll('[data-media-button]').forEach(el => el.setAttribute('aria-pressed', String(el.dataset.mediaButton === id)));

    }

    initializeTabs() {

      const tabs = [...this.querySelectorAll('[data-tab]')];

      this.querySelector('.oct-rum__tabs').setAttribute('role', 'tablist');

      tabs.forEach((tab, index) => {

        const panel = this.querySelector(`[data-panel="${tab.dataset.tab}"]`);

        tab.id = `${panel.id}-tab`;

        tab.setAttribute('role', 'tab');

        tab.setAttribute('aria-controls', panel.id);

        panel.setAttribute('role', 'tabpanel');

        panel.setAttribute('aria-labelledby', tab.id);

        panel.tabIndex = 0;

        tab.addEventListener('click', event => { event.preventDefault(); this.activateTab(tab.dataset.tab); });

        tab.addEventListener('keydown', event => {

          let next;

          if (event.key === 'ArrowRight') next = (index + 1) % tabs.length;

          if (event.key === 'ArrowLeft') next = (index + tabs.length - 1) % tabs.length;

          if (event.key === 'Home') next = 0;

          if (event.key === 'End') next = tabs.length - 1;

          if (next === undefined) return;

          event.preventDefault(); this.activateTab(tabs[next].dataset.tab); tabs[next].focus();

        });

      });

      this.querySelectorAll('[data-panel-link]').forEach(link => link.addEventListener('click', () => this.activateTab(link.dataset.panelLink)));

      const initial = tabs.find(tab => tab.hash === location.hash) || tabs[0];

      if (initial) this.activateTab(initial.dataset.tab);

    }

    activateTab(name) {

      this.querySelectorAll('[data-tab]').forEach(tab => {

        const active = tab.dataset.tab === name;

        tab.setAttribute('aria-selected', String(active)); tab.tabIndex = active ? 0 : -1;

      });

      this.querySelectorAll('[data-panel]').forEach(panel => { panel.hidden = panel.dataset.panel !== name; });

    }

  }

  customElements.define('oct-salford-rum', OctSalfordRum);

})();